// File: app/src/hooks/useWorkoutUiState.js
//
// Pure hook that derives the complete UI state for the active workout view
// from coachState + plan data. Extracted from CoachView.js to keep the
// container thin and the derivation testable.

import { useMemo } from "react";
import {
  toSeconds,
  calculateSmartRemaining,
  formatDisplayDuration,
} from "../utils/time";
import { buildAverageDurationMap } from "../utils/phaseLog";
import {
  formatNodeVal,
  formatNodeLoad,
  isTransitionNode,
  isSetupNode,
  PROTOCOL_WINDOW_LABEL_RE,
} from "../utils/planVisuals";
import { formatLabel } from "../utils/formatLabel";
import { findNodeByPath, isBikeNode, treeHasBike } from "../utils/workoutPlanUtils";
import { isCountedUnit, isTimedUnit } from "../utils/planNodeUtils";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Round to whole seconds up front so h/m/s are always integers. Callers pass
// derived values (e.g. Set/Phase elapsed = int session clock − a fractional
// wall-clock start marker in live-follower mode), which otherwise leak ~15
// floating-point decimals into the seconds field. (#39)
export const formatSeconds = (secs) => {
  const totalSecs = Math.max(0, Math.round(secs) || 0);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const clampProgress = (value) => Math.max(0, Math.min(1, value));

const TIME_UNITS = new Set([
  "s",
  "sec",
  "secs",
  "second",
  "seconds",
  "m",
  "min",
  "mins",
  "minute",
  "minutes",
  "h",
  "hr",
  "hrs",
  "hour",
  "hours",
]);

export const getRingSegmentType = (type) => {
  const normalized = String(type || "").toLowerCase();

  if (normalized.includes("count")) return "countin";
  if (normalized.includes("warm")) return "warm";
  if (normalized.includes("cool")) return "cool";
  if (normalized.includes("rest")) return "rest";
  if (normalized.includes("group")) return "group";
  if (normalized.includes("workout")) return "workout";

  return "work";
};

export const isStrictlyTimedBlockNode = (node) => {
  const unit = String(node?.unit || "").toLowerCase();
  if (!TIME_UNITS.has(unit)) return false;

  const durationSec = toSeconds(node?.value, node?.unit);
  return Number.isFinite(durationSec) && durationSec > 0;
};

// A time-capped container (AMRAP / "for time" cap, issue #36) is a Group whose
// unit is a time unit. Its children are a checklist of movements cycled for the
// whole duration, not individual timed phases.
export const isTimeCappedGroupNode = (node) =>
  node?.type === "Group" && ["s", "m"].includes(node?.unit);

// Build the Coach's static movement checklist for a time-capped block. Returns
// null for any non-time-capped node so the overlay only renders for AMRAP-style
// phases. Recovery children are excluded — the checklist lists movements only.
export const buildTimeCapChecklist = (node) => {
  if (!isTimeCappedGroupNode(node) || !Array.isArray(node.children)) return null;
  return node.children
    .filter(
      (c) =>
        c &&
        c.type !== "Rest" &&
        c.type !== "SetRest" &&
        c.type !== "Transition",
    )
    .map((c) => ({
      id: c.id,
      label: formatLabel(c.label || c.exercise_name || ""),
      detail: c.value ? formatNodeVal(c) : "",
    }));
};

export const buildBlockProgressItem = ({
  parentNode,
  currentNode,
  phaseTotalSec,
  timeLeftInPhase,
}) => {
  if (!parentNode?.children || parentNode.children.length < 2 || !currentNode) {
    return null;
  }

  const siblings = parentNode.children;
  const activeIndex = siblings.findIndex(
    (child) => child.id === currentNode.id,
  );
  if (activeIndex < 0) return null;

  const currentPhaseProgress =
    phaseTotalSec > 0
      ? clampProgress((phaseTotalSec - timeLeftInPhase) / phaseTotalSec)
      : 0;

  if (siblings.every(isStrictlyTimedBlockNode)) {
    return {
      proportionalSegments: siblings.map((child, index) => ({
        type: getRingSegmentType(child.type),
        duration: toSeconds(child.value, child.unit),
        completed: index < activeIndex,
        active: index === activeIndex,
        progress:
          index === activeIndex
            ? currentPhaseProgress
            : index < activeIndex
              ? 1
              : 0,
      })),
    };
  }

  return {
    segments: siblings.length,
    segmentItems: siblings.map((child) => ({
      type: getRingSegmentType(child.type),
    })),
    completed: activeIndex,
    active: activeIndex,
    currentProgress: isStrictlyTimedBlockNode(currentNode)
      ? currentPhaseProgress
      : 0,
    type: "group",
    discrete: !isStrictlyTimedBlockNode(currentNode),
  };
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const useWorkoutUiState = ({
  coachState,
  workoutPlan,
  phaseLogRef,
  countInRemaining,
  countInTotal,
  profile,
  historyMap,
}) => {
  const {
    currentNode,
    nextUpNode,
    timeLeftInPhase,
    progress,
    elapsedSessionTime,
    phaseStartElapsed,
    setStartElapsed,
    path,
    loopCounters,
    canGoBack,
    countUpMode,
  } = coachState;

  return useMemo(() => {
    if (!currentNode) return null;

    const isCountUp = !!countUpMode;
    const isRestPhase = currentNode.type.toLowerCase().includes("rest");
    const isTimed = isTimedUnit(currentNode.unit);
    // Counted = reps or calories: a self-paced target the user counts off, so
    // the ring pulses rather than counting down and the centre shows the target.
    const isCounted = isCountedUnit(currentNode.unit);
    const phaseType = currentNode.type.toLowerCase().split(" ")[0];

    // Time-capped container (AMRAP / "for time" cap, issue #36): a Group whose
    // unit is a time unit plays as a single continuous countdown, with its child
    // movements surfaced as a static checklist (not individual timed phases).
    const isTimeCappedGroup = isTimeCappedGroupNode(currentNode);
    const checklistItems = buildTimeCapChecklist(currentNode);

    const progressItems = [];

    // --- 1. OUTER RING (Current Phase) ---
    const phaseTotalSec = isTimed
      ? toSeconds(currentNode.value, currentNode.unit)
      : 0;

    if (isCountUp) {
      // Handled by spinnerMode in the presentation layer
    } else if (isTimed && phaseTotalSec > 0) {
      progressItems.push({
        label: "Time",
        current: isRestPhase
          ? timeLeftInPhase
          : phaseTotalSec - timeLeftInPhase,
        total: phaseTotalSec,
        type: phaseType,
        reverse: isRestPhase,
      });
    } else {
      // Counted phases get an indeterminate pulsing ring
      progressItems.push({
        label: isCounted ? "Count" : "Reps",
        current: 1,
        total: 1,
        type: phaseType,
        indeterminate: true,
      });
    }

    // Hijack outer ring for count-in
    const isCountingIn = countInRemaining > 0;
    if (isCountingIn) {
      progressItems.shift();
      progressItems.unshift({
        label: "Count-in",
        current: countInRemaining,
        total: countInTotal,
        type: "countin",
        countdown: true,
      });
    }

    // --- 2. MIDDLE RING (Exercises in Current Round) ---
    // Resolve parent via the tree path instead of currentNode.parent
    // (tree nodes do not carry back-references).
    const parentNode =
      path.length >= 2 ? findNodeByPath(workoutPlan, path.slice(0, -2)) : null;

    const blockProgressItem = buildBlockProgressItem({
      parentNode,
      currentNode,
      phaseTotalSec,
      timeLeftInPhase,
    });

    if (blockProgressItem) {
      progressItems.push(blockProgressItem);
    }

    // --- 3. INNER RING (Group Rounds) ---
    // progress is ordered deepest-first (via unshift in computeProgressItems).
    // Pick the deepest Group that actually repeats (total > 1) so the inner
    // ring shows meaningful round progress instead of a container Group with
    // value=1. Fall back to progress[0] if none repeat.
    if (progress?.length > 0) {
      const p = progress.find((g) => g.total > 1) || progress[0];
      const completed = p.restComplete
        ? Math.min(p.current, p.total)
        : p.current - 1;

      progressItems.push({
        segments: p.total,
        completed,
        active: p.current - 1,
        type: "workout",
        discrete: true,
      });
    }

    // --- Smart remaining time ---
    const avgRepMap = buildAverageDurationMap(phaseLogRef?.current || []);
    const phaseElapsed = elapsedSessionTime - phaseStartElapsed;
    const currentPhaseRemaining = isTimed
      ? timeLeftInPhase
      : Math.max(0, (avgRepMap[currentNode.id] || 0) - phaseElapsed);
    const totalRemaining = calculateSmartRemaining(
      workoutPlan,
      path,
      loopCounters,
      currentPhaseRemaining,
      { profile, historyMap, avgRepMap },
    );
    const endTimeStr =
      totalRemaining > 0
        ? new Date(Date.now() + totalRemaining * 1000).toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
          })
        : null;

    // --- Display values ---
    const displayValueStr = isCountUp
      ? formatSeconds(elapsedSessionTime)
      : isTimed
        ? new Date(timeLeftInPhase * 1000).toISOString().substr(14, 5)
        : isCounted
          ? String(currentNode.value)
          : "";
    const displaySubValueStr = isCounted ? currentNode.unit : null;

    const hasRoundSkip = progress?.some(
      (p) => p.type === "group" && p.total > 1,
    );

    // --- Context info ---
    const setElapsed = elapsedSessionTime - setStartElapsed;
    const contextParts = [];
    contextParts.push(`Total ${formatSeconds(elapsedSessionTime)}`);
    if (setStartElapsed !== phaseStartElapsed) {
      contextParts.push(`Set ${formatSeconds(setElapsed)}`);
    }
    if (isCounted) {
      contextParts.push(`Phase ${formatSeconds(phaseElapsed)}`);
    }
    const contextInfo = contextParts.join("  \u00B7  ");

    // --- Macro-progress (session overview) ---
    // Two-tier view consumed by MacroProgress:
    //   blocks          — top-level phases of the session (permanent map).
    //   activeGroupInfo — sets/rounds inside the active block, if any.
    // The inner concentric ring covers the deepest repeating Group; this
    // surfaces the level above it so context isn't lost during rests.
    const linkedIds = new Set((currentNode?.linkedNodes || []).map((n) => n.id));
    let blocks = [];
    let activeGroupInfo = null;

    const repeatingGroups = progress?.filter((g) => g.total > 1) || [];

    // Unwrap single-child container Groups to find distinct top-level phases.
    let planNode = workoutPlan;
    let pathDepth = 0;
    while (
      planNode?.children?.length === 1 &&
      planNode.children[0]?.type === "Group" &&
      planNode.children[0]?.children?.length > 1
    ) {
      planNode = planNode.children[0];
      pathDepth += 2; // skip one "children", idx pair
    }
    const topChildren = planNode?.children || [];
    const topActiveIndex =
      path.length >= pathDepth + 2 ? path[pathDepth + 1] : 0;

    if (topChildren.length > 1) {
      // Transitions are time-only and never shown in the macro stepper
      // (issue #22), but Setup sections stay. Filter transitions out while
      // keying each remaining block's state off its ORIGINAL index so the
      // active/completed mapping stays correct (the engine never lands on a
      // transition, so the active index always points at a kept block —
      // a real block or a Setup section).
      blocks = topChildren
        .map((child, i) => ({ child, i }))
        .filter(({ child }) => !isTransitionNode(child))
        .map(({ child, i }) => {
          // Setup sections stay in the stepper but render as a compact chapter
          // marker (a dot) rather than a full pill — their duration surfaces on
          // hover instead of taking a pill's worth of horizontal space.
          const setup = isSetupNode(child);
          return {
            label: child.label || child.type,
            type: child.type,
            isSetup: setup,
            durationLabel: setup
              ? formatDisplayDuration(toSeconds(child.value, child.unit))
              : null,
            // Linked steps (#177) are one phase, so every one of them lights.
            state:
              i === topActiveIndex || linkedIds.has(child.id)
                ? "active"
                : i < topActiveIndex
                  ? "completed"
                  : "future",
            node: child,
          };
        });
    } else if (repeatingGroups.length >= 2) {
      // Preset pattern with no distinct top-level phases — synthesise a single
      // block so the iteration tier still has a typed theme to inherit.
      blocks = [
        {
          label: planNode?.label || planNode?.type || "Workout",
          type: planNode?.type,
          state: "active",
          node: planNode,
        },
      ];
    }

    // We want the dots to represent the highest repeating group in the current
    // context. Since 'progress' is ordered deepest-first (Rounds -> Sets -> etc),
    // the highest repeating group is the last item in the 'repeatingGroups' array.
    if (repeatingGroups.length > 0) {
      const macroGroup = repeatingGroups[repeatingGroups.length - 1];
      activeGroupInfo = {
        label: macroGroup.label,
        totalIterations: macroGroup.total,
        currentIndex: Math.max(0, macroGroup.current - 1),
        phase: isRestPhase ? "rest" : "work",
      };
    }

    // Bike/trainer relevance: anywhere in the current top-level block, or the
    // rest/setup phase leading into a bike block (next phase is the bike node).
    const activeBlock = blocks.find((b) => b.state === "active");
    const bikeRelevantPhase =
      treeHasBike(activeBlock?.node) ||
      isBikeNode(nextUpNode) ||
      isBikeNode(currentNode);

    // A time-capped phase is an AMRAP cap unless the block names an EMOM-family
    // window, in which case the format token is the honest label (issue #63).
    const timeCapLabel = isTimeCappedGroup
      ? (PROTOCOL_WINDOW_LABEL_RE.exec(currentNode.label || "")?.[0] ?? "AMRAP")
      : null;

    return {
      phaseType: currentNode.type,
      phaseLabel: isCountingIn
        ? isRestPhase
          ? "UPCOMING: REST"
          : `UPCOMING: ${(timeCapLabel ?? currentNode.type).toUpperCase()}`
        : isRestPhase
          ? "REST"
          : (timeCapLabel?.toUpperCase() ?? currentNode.type.toUpperCase()),
      exerciseName: currentNode.label,
      // Static checklist of movements for a time-capped block (#36); null
      // otherwise so the Coach only renders the overlay for AMRAP-style phases.
      checklistItems,
      exerciseDescription: currentNode.description,
      repDisplay: isCounted
        ? `${currentNode.value} ${currentNode.unit}`
        : null,
      // The load for the set actually in progress. `currentNode` is the
      // compiler's per-set-resolved node, so a ramp reports 90 kg on set 3, not
      // set 1's 80 kg. Suppressed on recovery phases, which carry no load.
      loadDisplay: isRestPhase ? null : formatNodeLoad(currentNode) || null,
      intensityTarget: currentNode?.intensityTarget,
      progressItems,
      displayValue: isCountingIn
        ? countInRemaining.toString()
        : displayValueStr,
      displaySubValue: isCountingIn ? "GET READY" : displaySubValueStr,
      // Named for reps by history; it means "self-paced target" — the Coach
      // reads it to swap the countdown for a Done button, which is exactly what
      // a calorie target needs too.
      isRepBased: isCountingIn ? false : isCounted,
      isCountUp: isCountingIn ? false : isCountUp,
      // Inter-block Setup section — the Coach surfaces a compact countdown, a
      // skip button, and the upcoming-block outline. Suppressed during count-in
      // so the "get ready" overlay isn't dressed up as a setup screen. (Plain
      // transitions never reach here — the engine skips them. Issue #22.)
      isSetupScreen: !isCountingIn && isSetupNode(currentNode),
      canGoBack,
      hasRoundSkip,
      nextUpLabel: nextUpNode?.label,
      nextUpValue: nextUpNode?.value ? formatNodeVal(nextUpNode) : null,
      totalRemaining,
      endTimeStr,
      contextInfo,
      blocks,
      activeGroupInfo,
      bikeRelevantPhase,
    };
  }, [
    currentNode,
    timeLeftInPhase,
    progress,
    elapsedSessionTime,
    phaseStartElapsed,
    setStartElapsed,
    path,
    loopCounters,
    canGoBack,
    countUpMode,
    nextUpNode,
    workoutPlan,
    phaseLogRef,
    countInRemaining,
    countInTotal,
    profile,
    historyMap,
  ]);
};

export default useWorkoutUiState;
